import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { MIN_PASSWORD_LENGTH } from '@promanage/core';
import { Button, Field, Input } from '@promanage/ui';
import { AuthCard } from '../features/auth/AuthCard';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/set-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: SetPassword,
});

/**
 * An invited user choosing their first password.
 *
 * The token in the URL IS the authentication — there is no session yet — so it is single-use
 * and the server consumes it in the same statement that sets the password. Nothing here
 * confirms whether a token is valid before it is spent: probing "is this a real invite" is
 * exactly what an attacker with a leaked link wants, and the answer would be the same
 * expired-or-used message either way.
 *
 * Note the administrator who invited this person never learns the password. That is the point
 * of the flow: a compromised admin account cannot quietly become its own new staff member.
 */
function SetPassword() {
  const { token } = Route.useSearch();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState<string | undefined>();

  const complete = useMutation(
    trpc.auth.completeSetup.mutationOptions({
      meta: { errorTitle: 'Could not set that password', credentials: true },
    }),
  );

  if (!token) {
    return (
      <AuthCard
        title="This link is incomplete"
        intro="Open the invitation link exactly as it was sent to you — the part after the question mark is what identifies the invitation."
      >
        <Link to="/login">
          <Button variant="secondary" className="w-full">
            Go to sign in
          </Button>
        </Link>
      </AuthCard>
    );
  }

  if (complete.data) {
    return (
      <AuthCard
        title="Password set"
        intro={`You can now sign in as ${complete.data.email}. If your role requires it, you will be asked to set up an authenticator app next.`}
      >
        <Link to="/login">
          <Button size="lg" className="w-full">
            Sign in
          </Button>
        </Link>
      </AuthCard>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    // Checked here only to save a round trip on an obvious slip. Everything that actually
    // decides whether the password is acceptable — length, breach corpus, whether it contains
    // their own name — is judged by the server, which is the only side that can be trusted to.
    if (password !== confirmation) {
      setMismatch('Both passwords must match');
      return;
    }
    setMismatch(undefined);
    complete.mutate({ token, password });
  };

  return (
    <AuthCard
      title="Choose a password"
      intro="This is for your ProManage account. Use a password you do not use anywhere else — a manager will remember it for you."
      footer="ProManage will never ask for this password by email or phone."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field
          label="New password"
          required
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Length beats punctuation.`}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Field label="Repeat password" required error={mismatch}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => {
                setConfirmation(e.target.value);
                setMismatch(undefined);
              }}
            />
          )}
        </Field>

        <Button type="submit" size="lg" disabled={complete.isPending}>
          {complete.isPending ? 'Saving' : 'Set password'}
        </Button>
      </form>
    </AuthCard>
  );
}
